import React, { useState } from "react";
import { CheckCircle, Calendar, User, Wallet, Receipt } from "lucide-react";

function formatCurrency(value) {
  if (value === null || value === undefined) return "–";
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatDate(dateStr) {
  if (!dateStr) return "–";
  // Truncates the time component from "2026-03-17T00:00:00"
  return dateStr.split("T")[0];
}

export default function SalesDetailTable({
  detailedData = [],
  context = null,
  onClose,
}) {
  const [expandedRow, setExpandedRow] = useState(null);

  if (!detailedData || detailedData.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-slate-400">
        <CheckCircle size={32} className="mb-2 text-slate-300" />
        <p className="text-xs">No detailed transaction records found for this period.</p>
      </div>
    );
  }

  const totalAmount = detailedData.reduce(
    (sum, item) => sum + (item.AMOUNT || 0),
    0
  );
  const totalVat = detailedData.reduce(
    (sum, item) => sum + (item.VATAMT || 0),
    0
  );

  return (
    <div className="p-3 sm:p-6 space-y-3 sm:space-y-4">
      {/* Top summary panel */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
        {/* Period / Context card */}
        <div className="text-center p-3 sm:p-4 bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center justify-center gap-1">
            <Calendar size={11} />
            Period
          </p>
          <p className="text-sm font-extrabold text-slate-900 mt-1 truncate">
            {context?.month || "–"}
          </p>
          {context?.salesperson && (
            <p className="text-[14px] text-slate-500 mt-0.5 flex items-center justify-center gap-1 truncate">
              <User size={15}  />
              {context.salesperson}
            </p>
          )}
        </div>

        {/* Total Amount card */}
        <div className="text-center p-3 sm:p-4 bg-gradient-to-r from-indigo-50 to-violet-50 rounded-xl">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center justify-center gap-1">
            <Wallet size={11} />
            Total Amount
          </p>
          <p className="text-xl sm:text-2xl font-extrabold text-slate-900 mt-1">
            {formatCurrency(totalAmount)}
          </p>
        </div>

        {/* Total VAT card */}
        <div className="text-center p-3 sm:p-4 bg-gradient-to-r from-sky-50 to-cyan-50 rounded-xl">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center justify-center gap-1">
            <Receipt size={11} />
            Total VAT Amount
          </p>
          <p className="text-xl sm:text-2xl font-extrabold text-slate-900 mt-1">
            {formatCurrency(totalVat)}
          </p>
        </div>
      </div>

      {/* Table */}
      {/* Column order is deliberate: Date, Customer, Amount are the columns that
          must be visible without scrolling on a phone. VAT / Type are less
          critical so they sit past the fold and are reached by scrolling right. */}
      <div className="overflow-hidden rounded-xl border border-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <colgroup>
              <col className="w-[68px] sm:w-auto" />
              <col className="w-[112px] sm:w-auto" />
              <col className="w-[84px] sm:w-auto" />
              <col className="w-[84px] sm:w-auto" />
              <col className="w-[64px] sm:w-auto" />
            </colgroup>
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-2 py-2 sm:px-3 sm:py-2 text-left text-[9px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                  Date
                </th>
                <th className="px-2 py-2 sm:px-3 sm:py-2 text-left text-[9px] font-bold text-slate-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-2 py-2 sm:px-3 sm:py-2 text-right text-[9px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                  Amount
                </th>
                <th className="px-2 py-2 sm:px-3 sm:py-2 text-right text-[9px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                  VAT Amount
                </th>
                <th className="px-2 py-2 sm:px-3 sm:py-2 text-left text-[9px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                  Type
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {detailedData.map((item, index) => (
                <tr
                  key={item["INV NO"] || index}
                  className="hover:bg-slate-50 transition-colors"
                >
                  <td className="px-2 py-2.5 sm:px-3 sm:py-3 whitespace-nowrap">
                    <span className="text-[10px] font-medium text-slate-600">
                      {formatDate(item["INV DATE"])}
                    </span>
                  </td>
                  <td className="px-2 py-2.5 sm:px-3 sm:py-3">
                    <p
                      onClick={() =>
                        setExpandedRow(expandedRow === index ? null : index)
                      }
                      title={item.CUSTOMER}
                      className={
                        expandedRow === index
                          ? "text-[10px] font-semibold text-slate-800 whitespace-normal break-words cursor-pointer"
                          : "text-[10px] font-semibold text-slate-800 truncate max-w-[100px] sm:max-w-[220px] cursor-pointer underline decoration-dotted decoration-slate-300 underline-offset-2"
                      }
                    >
                      {item.CUSTOMER} 
                    </p>
                  </td>
                  <td className="px-2 py-2.5 sm:px-3 sm:py-3 text-right whitespace-nowrap">
                    <span className="text-[10px] font-bold text-indigo-600">
                      {formatCurrency(item.AMOUNT)}
                    </span>
                  </td>
                  <td className="px-2 py-2.5 sm:px-3 sm:py-3 text-right whitespace-nowrap">
                    <span className="text-[10px] font-medium text-slate-600">
                      {formatCurrency(item.VATAMT)}
                    </span>
                  </td>
                  <td className="px-2 py-2.5 sm:px-3 sm:py-3 whitespace-nowrap">
                    <span
                      className={`text-[9px] font-semibold px-2 py-0.5 rounded-full ${
                        item.CASHCRED === "R"
                          ? "bg-amber-100 text-amber-700"
                          : "bg-sky-100 text-sky-700"
                      }`}
                    >
                      {item.CASHCRED === "R"
                        ? "Credit"
                        : "Cash" || "Other"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-50 border-t-2 border-slate-200">
                <td
                  colSpan="2"
                  className="px-2 py-2.5 sm:px-3 sm:py-3 text-right text-[10px] font-bold text-slate-600 uppercase tracking-wider whitespace-nowrap"
                >
                  Total
                </td>
                <td className="px-2 py-2.5 sm:px-3 sm:py-3 text-right whitespace-nowrap">
                  <span className="text-xs font-extrabold text-indigo-600">
                    {formatCurrency(totalAmount)}
                  </span>
                </td>
                <td className="px-2 py-2.5 sm:px-3 sm:py-3 text-right whitespace-nowrap">
                  <span className="text-[10px] font-bold text-slate-700">
                    {formatCurrency(totalVat)}
                  </span>
                </td>
                <td className="px-2 py-2.5 sm:px-3 sm:py-3" />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}